import {
    Banner1,
    BannerCarousel1,
    BannerCarousel2,
    BannerCarousel3,
    ProductCarousel1,
    ProductCarousel2,
    ProductCarousel3,
    ProductSpotlight,
} from 'components/storefront/StorefrontConfigEditor/slices';

/**
 * Default form data structure for a new storefront configuration.
 * Used when creating a new configuration in the StorefrontConfigEditor.
 *
 * @constant {Object}
 * @property {string} name - Display name for the configuration
 * @property {boolean} published - Whether the configuration is currently published
 * @property {Array} slices - Array of content slice objects that make up the configuration
 */
export const DEFAULT_FORM_DATA = Object.freeze({
    name: 'New Configuration',
    published: false,
    slices: [],
});

/**
 * @typedef {Object} StorefrontSlice
 * @property {string} category - Category grouping for the slice (e.g. 'Banner', 'Product', etc.)
 * @property {Object} defaultSlice - Default configuration object for this slice type
 * @property {string} description - Brief description of what the slice does and when to use it
 * @property {string} displayName - Human-readable name for the slice type
 * @property {string} [productListKey] - Optional key that identifies where the product list is stored within the slice data
 * @property {string} thumbnail - Preview image URL showing an example of the slice
 * @property {string} displayType - String identifier for the slice type
 * @property {function(Object): boolean} validateSlice - Function to validate slice data
 */

/**
 * Registry mapping slice type identifiers to their corresponding component implementations.
 *
 * Used by the StorefrontConfigEditor to:
 * 1. Render the appropriate component for each slice type
 * 2. Access slice-specific validation rules and default values
 * 3. Build new slices with the correct structure
 *
 * @constant {Object.<string, StorefrontSlice>}
 */
export const SLICE_REGISTRY = Object.freeze({
    [Banner1.displayType]: Banner1,
    [BannerCarousel1.displayType]: BannerCarousel1,
    [BannerCarousel2.displayType]: BannerCarousel2,
    [BannerCarousel3.displayType]: BannerCarousel3,
    [ProductCarousel1.displayType]: ProductCarousel1,
    [ProductCarousel2.displayType]: ProductCarousel2,
    [ProductCarousel3.displayType]: ProductCarousel3,
    [ProductSpotlight.displayType]: ProductSpotlight,
});

/**
 * Array of slice display types that support visibility toggling in the storefront.
 * These slices can be shown or hidden via the visibility toggle button in the editor.
 *
 * @constant {string[]}
 */
export const CAROUSEL_TYPES_WITH_VISIBILITY = Object.freeze([
    ProductCarousel1.displayType,
    ProductCarousel2.displayType,
    ProductCarousel3.displayType,
]);

/**
 * Product carousel display types that share a compatible config schema
 * and can be switched between in the storefront config editor.
 *
 * @constant {string[]}
 */
export const PRODUCT_CAROUSEL_TYPES = Object.freeze([
    ProductCarousel1.displayType,
    ProductCarousel2.displayType,
    ProductCarousel3.displayType,
]);
